import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { cortexMemoryApi, memoryApi, type CortexMemoryItem } from '@/api/cortex.api';
import { create } from '@bufbuild/protobuf';
import { ListRequestSchema } from '@/proto/agentplatform/httpv2/common_pb';
import { MemorySchema, type Memory } from '@/proto/agentplatform/v1/memory_pb';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from '@/components/ui/alert-dialog';
import {
  MemoryIcon,
  PlusIcon,
  TrashIcon,
  PencilIcon,
  LoaderIcon,
  AlertCircleIcon,
  EyeIcon,
} from '@/components/icons';
import { MEMORY_PROVIDER_DEFAULT } from '@/constants/memory';

const STORE_KEY = 'memory.activeStoreId';
const LIMIT_KEY = 'memory.limit';

function formatDate(s?: string | null): string {
  if (!s) return '-';
  try {
    return new Date(s).toLocaleString('zh-CN');
  } catch {
    return s;
  }
}

function extractTags(metadata?: Record<string, unknown> | null): string[] {
  if (!metadata) return [];
  const raw = metadata.tags;
  if (Array.isArray(raw)) return raw.map(String);
  return [];
}

function CopyButton({ text, label }: { text: string; label: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded hover:bg-accent text-muted-foreground hover:text-foreground transition-colors"
      onClick={() => {
        navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }}
    >
      <svg
        className="h-3.5 w-3.5"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={2}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
        />
      </svg>
      {copied ? '已复制' : label}
    </button>
  );
}

export default function MemoriesPage({ embedded = false }: { embedded?: boolean }) {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  // ==================== 记忆实例状态 ====================
  const [activeStoreId, setActiveStoreId] = useState<string | null>(
    () => localStorage.getItem(STORE_KEY) || null
  );
  const [showStoreManage, setShowStoreManage] = useState(false);
  const [showStoreForm, setShowStoreForm] = useState(false);
  const [editingStore, setEditingStore] = useState<Memory | null>(null);
  const [storeFormData, setStoreFormData] = useState({ name: '', description: '' });

  // ==================== 记忆状态 ====================
  const [limit, setLimit] = useState(() => Number(localStorage.getItem(LIMIT_KEY) || '100'));
  const searchTab = (searchParams.get('search') as 'text' | 'id') || 'text';
  const [searchQuery, setSearchQuery] = useState('');
  const [memoryIdInput, setMemoryIdInput] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<CortexMemoryItem[] | null>(null);
  const [searchError, setSearchError] = useState('');
  const [lastOp, setLastOp] = useState<'list' | 'search' | 'id' | null>(null);

  // 弹窗状态
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingItem, setEditingItem] = useState<CortexMemoryItem | null>(null);
  const [formMemory, setFormMemory] = useState('');
  const [formInfer, setFormInfer] = useState(true);

  const [deleteTarget, setDeleteTarget] = useState<CortexMemoryItem | null>(null);
  const [detailItem, setDetailItem] = useState<CortexMemoryItem | null>(null);
  const [expandedDetails, setExpandedDetails] = useState<Record<string, boolean>>({});

  // ==================== 持久化 ====================
  useEffect(() => {
    if (activeStoreId) {
      localStorage.setItem(STORE_KEY, activeStoreId);
    } else {
      localStorage.removeItem(STORE_KEY);
    }
  }, [activeStoreId]);
  useEffect(() => {
    if (limit > 0) localStorage.setItem(LIMIT_KEY, String(limit));
  }, [limit]);

  // ==================== 记忆实例加载 ====================
  const { data: memoriesListData, isLoading: storesLoading } = useQuery({
    queryKey: ['memory-instances'],
    queryFn: () =>
      memoryApi.listMemories(
        create(ListRequestSchema, {
          page: 1,
          pageSize: 200,
        })
      ),
  });
  const subStores = memoriesListData?.data ?? [];

  useEffect(() => {
    if (storesLoading || subStores.length === 0) return;
    const ids = subStores.map((s) => s.id);
    if (!activeStoreId || !ids.includes(activeStoreId)) {
      setActiveStoreId(subStores[0].id);
    }
  }, [subStores, storesLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  const activeStore = subStores.find((s) => s.id === activeStoreId) || null;
  const hasStore = !!activeStoreId && !!activeStore;

  // ==================== 记忆实例操作 ====================
  const handleOpenCreateStore = () => {
    setEditingStore(null);
    setStoreFormData({ name: '', description: '' });
    setShowStoreForm(true);
  };

  const handleOpenEditStore = (store: Memory) => {
    setEditingStore(store);
    setStoreFormData({ name: store.name, description: store.description || '' });
    setShowStoreForm(true);
  };

  const storeSubmitMutation = useMutation({
    mutationFn: async () => {
      if (editingStore) {
        return memoryApi.updateMemory(editingStore.id, {
          ...editingStore,
          name: storeFormData.name,
          description: storeFormData.description || '',
        });
      } else {
        return memoryApi.createMemory(
          create(MemorySchema, {
            name: storeFormData.name,
            description: storeFormData.description || '',
            provider: MEMORY_PROVIDER_DEFAULT,
          })
        );
      }
    },
    onSuccess: (result) => {
      setShowStoreForm(false);
      if (!editingStore && result?.id) {
        setActiveStoreId(result.id);
      }
      queryClient.invalidateQueries({ queryKey: ['memory-instances'] });
    },
  });

  const storeDeleteMutation = useMutation({
    mutationFn: (storeId: string) => memoryApi.deleteMemory(storeId),
    onSuccess: (_, storeId) => {
      if (activeStoreId === storeId) {
        setActiveStoreId(null);
        setSearchResults(null);
      }
      queryClient.invalidateQueries({ queryKey: ['memory-instances'] });
    },
  });

  // ==================== 记忆加载 ====================
  const {
    data: memoriesData,
    isLoading,
    error: listError,
    refetch,
  } = useQuery({
    queryKey: ['cortex-memory-items', activeStoreId, limit],
    queryFn: () => cortexMemoryApi.listItems(activeStoreId!, limit),
    enabled: !!activeStoreId,
  });

  const memories = useMemo(() => {
    const raw = searchResults ?? memoriesData?.items ?? [];
    if (searchResults) return raw;
    return [...raw].sort(
      (a, b) => new Date(b.created_at ?? 0).getTime() - new Date(a.created_at ?? 0).getTime()
    );
  }, [searchResults, memoriesData]);

  const tagStats = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const m of memories) {
      for (const tag of extractTags(m.metadata)) {
        counts[tag] = (counts[tag] || 0) + 1;
      }
    }
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 30);
  }, [memories]);

  const setSearchTab = (tab: 'text' | 'id') => {
    setSearchParams({ search: tab });
    setSearchResults(null);
    setSearchError('');
  };

  const searchTimerRef = useRef<number | undefined>(undefined);

  const doSearch = useCallback(async () => {
    if (!activeStoreId) return;
    if (searchTab === 'id') {
      const id = memoryIdInput.trim();
      if (!id) return;
      setIsSearching(true);
      setSearchError('');
      try {
        const item = await cortexMemoryApi.getItem(id, activeStoreId);
        setSearchResults([item]);
        setLastOp('id');
      } catch (err: unknown) {
        setSearchError(err instanceof Error ? err.message : String(err));
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
      return;
    }
    const q = searchQuery.trim();
    if (!q) {
      setSearchResults(null);
      setLastOp('list');
      refetch();
      return;
    }
    setIsSearching(true);
    setSearchError('');
    try {
      const resp = await cortexMemoryApi.search({
        query: q,
        store_id: activeStoreId,
        limit,
      });
      setSearchResults(resp.items || []);
      setLastOp('search');
    } catch (err: unknown) {
      setSearchError(err instanceof Error ? err.message : String(err));
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  }, [activeStoreId, searchTab, searchQuery, memoryIdInput, limit, refetch]);

  useEffect(() => {
    if (searchTab !== 'text' || !searchQuery.trim()) return;
    window.clearTimeout(searchTimerRef.current);
    searchTimerRef.current = window.setTimeout(() => {
      doSearch();
    }, 400);
    return () => window.clearTimeout(searchTimerRef.current);
  }, [searchQuery, limit]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (activeStoreId && !storesLoading) {
      setSearchResults(null);
      setLastOp('list');
      refetch();
    }
  }, [activeStoreId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ==================== 记忆 CRUD ====================
  const resetForm = () => {
    setFormMemory('');
    setFormInfer(true);
  };

  const openCreate = () => {
    resetForm();
    setShowCreateDialog(true);
  };

  const openEdit = (item: CortexMemoryItem) => {
    setFormMemory(item.memory);
    setEditingItem(item);
  };

  const createMutation = useMutation({
    mutationFn: () =>
      cortexMemoryApi.createItem({
        memory: formMemory,
        store_id: activeStoreId!,
        infer: formInfer,
      }),
    onSuccess: () => {
      setShowCreateDialog(false);
      resetForm();
      queryClient.invalidateQueries({ queryKey: ['cortex-memory-items'] });
    },
  });

  const updateMutation = useMutation({
    mutationFn: () => cortexMemoryApi.updateItem(editingItem!.id, activeStoreId!, formMemory),
    onSuccess: () => {
      setEditingItem(null);
      resetForm();
      queryClient.invalidateQueries({ queryKey: ['cortex-memory-items'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => cortexMemoryApi.deleteItem(deleteTarget!.id, activeStoreId!),
    onSuccess: () => {
      const target = deleteTarget;
      setDeleteTarget(null);
      queryClient.invalidateQueries({ queryKey: ['cortex-memory-items'] });
      if (searchResults && target) {
        setSearchResults((prev) => prev?.filter((m) => m.id !== target.id) || null);
      }
    },
  });

  const toggleDetails = (id: string) => {
    setExpandedDetails((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <div className={embedded ? 'space-y-6' : 'p-6 space-y-6 max-w-[1400px] mx-auto'}>
      {!embedded && (
        <header>
          <h1 className="text-3xl font-bold tracking-tight">记忆管理</h1>
        </header>
      )}

      <div className="grid grid-cols-12 gap-6">
        {/* 侧边栏 */}
        <div className="col-span-12 md:col-span-3 space-y-4">
          <div className="bg-background rounded-xl border shadow-sm p-4 space-y-4">
            <div className="flex items-center gap-2 text-base font-semibold">
              <MemoryIcon className="h-4 w-4 text-primary" />
              记忆管理
            </div>

            {/* 记忆库选择 */}
            <div className="space-y-2">
              <label className="text-xs text-muted-foreground block">记忆库</label>
              {storesLoading ? (
                <div className="h-9 w-full rounded-md bg-muted animate-pulse" />
              ) : subStores.length > 0 ? (
                <div className="flex items-center gap-1">
                  <div className="flex-1 min-w-0">
                    <Select value={activeStoreId || ''} onValueChange={setActiveStoreId}>
                      <SelectTrigger className="w-full h-9">
                        <SelectValue placeholder="选择记忆库...">
                          {activeStore ? (
                            <span
                              className="block truncate"
                              title={`${activeStore.name}（${activeStore.id}）`}
                            >
                              {`${activeStore.name}（${activeStore.id}）`}
                            </span>
                          ) : (
                            ''
                          )}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        {subStores.map((store) => (
                          <SelectItem key={store.id} value={store.id} className="max-w-full">
                            <div className="min-w-0">
                              <div className="truncate" title={store.name}>
                                {store.name}
                              </div>
                              <div className="text-xs text-muted-foreground font-mono truncate">
                                {store.id}
                              </div>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="shrink-0 h-9 w-9 p-0"
                    onClick={() => setShowStoreManage(true)}
                    title="管理记忆库"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    </svg>
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground">暂无记忆库</p>
                  <Button
                    size="sm"
                    variant="outline"
                    className="w-full"
                    onClick={() => setShowStoreManage(true)}
                  >
                    管理记忆库
                  </Button>
                </div>
              )}
              {activeStore && (
                <div className="mt-2 text-xs text-muted-foreground space-y-1">
                  <div>
                    ID: <span className="font-mono break-all">{activeStore.id}</span>
                  </div>
                  {activeStore.description && (
                    <div className="break-all">描述: {activeStore.description}</div>
                  )}
                </div>
              )}
            </div>

            {hasStore && (
              <>
                {/* 状态 */}
                <div className="border-t pt-4 space-y-2">
                  <label className="text-xs text-muted-foreground block">状态</label>
                  <div>
                    {isLoading || isSearching ? (
                      <Badge variant="outline">正在加载...</Badge>
                    ) : memories.length > 0 ? (
                      <Badge
                        variant="secondary"
                        className="bg-green-600/10 text-green-700 dark:text-green-400"
                      >
                        结果就绪
                      </Badge>
                    ) : (
                      <Badge variant="outline">等待搜索</Badge>
                    )}
                  </div>
                </div>

                {/* 快速操作 */}
                <div className="border-t pt-4 space-y-2">
                  <label className="text-xs text-muted-foreground block">快速操作</label>
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" onClick={openCreate} disabled={isLoading}>
                      <PlusIcon className="h-4 w-4 mr-1" />
                      新增记忆
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => doSearch()}
                      disabled={isLoading}
                    >
                      <svg
                        className="h-4 w-4 mr-1"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                        />
                      </svg>
                      刷新
                    </Button>
                  </div>
                </div>

                {/* 标签统计 */}
                {tagStats.length > 0 && (
                  <div className="border-t pt-4 space-y-2">
                    <label className="text-xs text-muted-foreground block">标签统计</label>
                    <div className="flex flex-wrap gap-1.5 max-h-48 overflow-auto">
                      {tagStats.map(([tag, count]) => (
                        <Badge
                          key={tag}
                          variant="outline"
                          className="cursor-pointer hover:bg-accent text-xs"
                          onClick={() => setSearchQuery(tag)}
                        >
                          #{tag} <span className="ml-1 text-muted-foreground">({count})</span>
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* 内容区 */}
        <div className="col-span-12 md:col-span-9 space-y-6">
          {/* 无记忆库时的空状态 */}
          {!storesLoading && !hasStore && (
            <div className="bg-background rounded-xl border shadow-sm p-16 text-center space-y-4">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-16 w-16 mx-auto text-muted-foreground/40"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.5}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                />
              </svg>
              <h3 className="text-lg font-semibold">请先创建记忆库</h3>
              <p className="text-muted-foreground">
                每个记忆库对应一个独立的向量数据库集合，用于隔离不同场景的记忆数据
              </p>
              <Button onClick={handleOpenCreateStore}>
                <PlusIcon className="h-4 w-4 mr-2" />
                创建第一个记忆库
              </Button>
            </div>
          )}

          {/* 有记忆库时显示记忆管理内容 */}
          {hasStore && (
            <>
              {/* 搜索栏 */}
              <div className="bg-background rounded-xl border shadow-sm p-4">
                <div className="flex border-b mb-4">
                  {(['text', 'id'] as const).map((t) => (
                    <button
                      key={t}
                      className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                        searchTab === t
                          ? 'border-primary text-primary'
                          : 'border-transparent text-muted-foreground hover:text-foreground'
                      }`}
                      onClick={() => setSearchTab(t)}
                    >
                      {t === 'text' ? '检索记忆内容' : '通过记忆 ID 获取'}
                    </button>
                  ))}
                </div>

                {searchTab === 'text' ? (
                  <div className="space-y-3">
                    <div className="space-y-1.5">
                      <label className="text-sm font-medium block">搜索内容</label>
                      <Input
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="输入文本，检索相关的记忆内容..."
                        onKeyDown={(e) => e.key === 'Enter' && doSearch()}
                      />
                    </div>
                    <div className="flex flex-wrap items-center gap-3">
                      <div className="flex items-center gap-2">
                        <label className="text-sm whitespace-nowrap">返回条数限制</label>
                        <Input
                          type="number"
                          value={limit}
                          onChange={(e) => setLimit(Number(e.target.value) || 100)}
                          className="w-28"
                        />
                      </div>
                      <Button variant="outline" onClick={() => doSearch()} disabled={isSearching}>
                        <svg
                          className="h-4 w-4 mr-1"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                          />
                        </svg>
                        {isSearching ? '加载中...' : '搜索 / 刷新'}
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      搜索使用向量相似度检索，留空时返回全部列表
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div className="space-y-1.5">
                        <label className="text-sm font-medium block">记忆 ID</label>
                        <Input
                          value={memoryIdInput}
                          onChange={(e) => setMemoryIdInput(e.target.value)}
                          placeholder="输入记忆ID..."
                          onKeyDown={(e) => e.key === 'Enter' && doSearch()}
                        />
                      </div>
                    </div>
                    <Button variant="outline" onClick={() => doSearch()} disabled={isSearching}>
                      <svg
                        className="h-4 w-4 mr-1"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                        />
                      </svg>
                      {isSearching ? '加载中...' : '获取'}
                    </Button>
                  </div>
                )}
              </div>

              {/* 结果计数 */}
              <div className="text-center text-sm text-muted-foreground">
                当前结果共 {memories.length} 条{lastOp === 'list' && '，按时间从新到旧排序'}
                {lastOp === 'search' && '，按分数从高到低排序'}
              </div>

              {/* 错误提示 */}
              {(searchError || (listError && !searchResults)) && (
                <div className="bg-background rounded-xl border border-destructive/50 shadow-sm p-4">
                  <div className="flex items-start gap-2 text-destructive">
                    <AlertCircleIcon className="h-4 w-4 mt-0.5 shrink-0" />
                    <p className="text-sm">
                      {searchError || (listError instanceof Error ? listError.message : '加载失败')}
                    </p>
                  </div>
                </div>
              )}

              {/* 加载状态 */}
              {isLoading && memories.length === 0 && !searchResults && (
                <div className="space-y-3">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <div key={i} className="bg-background rounded-xl border shadow-sm p-6">
                      <div className="h-20 rounded-md bg-muted animate-pulse" />
                    </div>
                  ))}
                </div>
              )}

              {/* 空状态 */}
              {!isLoading && memories.length === 0 && !searchError && !listError && (
                <div className="bg-background rounded-xl border shadow-sm p-12 text-center space-y-4">
                  <MemoryIcon className="h-12 w-12 mx-auto text-muted-foreground/40" />
                  <p className="text-muted-foreground">
                    {searchResults ? '未找到匹配的记忆' : '暂无记忆数据'}
                  </p>
                  <Button onClick={openCreate}>
                    <PlusIcon className="h-4 w-4 mr-1" />
                    创建第一条记忆
                  </Button>
                </div>
              )}

              {/* 记忆卡片列表 */}
              <div className="space-y-3">
                {memories.map((m) => (
                  <div
                    key={m.id}
                    className="bg-background rounded-xl border shadow-sm p-5 hover:shadow-md transition-shadow space-y-3"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        {extractTags(m.metadata).length > 0 && (
                          <div className="flex flex-wrap gap-1.5 mb-2">
                            {extractTags(m.metadata).map((tag) => (
                              <Badge key={tag} variant="outline" className="text-xs">
                                #{tag}
                              </Badge>
                            ))}
                          </div>
                        )}
                        {m.id && (
                          <div className="text-xs text-muted-foreground">
                            ID: <span className="font-mono break-all">{m.id}</span>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground shrink-0">
                        {typeof m.score === 'number' && (
                          <Badge
                            variant="outline"
                            className="border-yellow-500 bg-yellow-50 text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-400"
                          >
                            score: {m.score.toFixed(3)}
                          </Badge>
                        )}
                        <span>{formatDate(m.created_at)}</span>
                      </div>
                    </div>

                    <p className="text-sm leading-relaxed whitespace-pre-wrap break-words">
                      {m.memory}
                    </p>

                    <button
                      className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                      onClick={() => toggleDetails(m.id)}
                    >
                      {expandedDetails[m.id] ? '收起详细信息' : '展开详细信息'}
                    </button>

                    {expandedDetails[m.id] && (
                      <pre className="text-xs text-muted-foreground bg-muted p-3 rounded-md overflow-auto max-h-72">
                        {JSON.stringify(m, null, 2)}
                      </pre>
                    )}

                    <div className="flex flex-wrap justify-end gap-2 pt-3 border-t">
                      <CopyButton text={m.id} label="复制ID" />
                      <CopyButton text={m.memory} label="复制内容" />
                      <button
                        onClick={() => setDetailItem(m)}
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded hover:bg-accent text-muted-foreground hover:text-foreground transition-colors"
                      >
                        <EyeIcon className="h-3.5 w-3.5" />
                        查看
                      </button>
                      <button
                        onClick={() => openEdit(m)}
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded hover:bg-accent text-muted-foreground hover:text-foreground transition-colors"
                      >
                        <PencilIcon className="h-3.5 w-3.5" />
                        编辑
                      </button>
                      <button
                        onClick={() => setDeleteTarget(m)}
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded hover:bg-accent text-muted-foreground hover:text-destructive transition-colors"
                      >
                        <TrashIcon className="h-3.5 w-3.5" />
                        删除
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* 记忆库管理弹窗 */}
      <Dialog open={showStoreManage} onOpenChange={setShowStoreManage}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>记忆库管理</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="flex justify-end">
              <Button
                size="sm"
                onClick={() => {
                  setShowStoreManage(false);
                  handleOpenCreateStore();
                }}
              >
                <PlusIcon className="h-4 w-4 mr-1" />
                新建记忆库
              </Button>
            </div>
            {subStores.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-6">
                暂无记忆库，请点击上方按钮创建
              </p>
            ) : (
              <div className="max-h-80 overflow-auto space-y-2">
                {subStores.map((store) => (
                  <div
                    key={store.id}
                    className={`flex items-center justify-between p-3 rounded-md border ${
                      store.id === activeStoreId ? 'border-primary bg-accent/50' : 'border-border'
                    }`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium truncate">{store.name}</div>
                      <div className="text-xs text-muted-foreground font-mono break-all">
                        {store.id}
                      </div>
                      {store.description && (
                        <div className="text-xs text-muted-foreground mt-0.5 break-all">
                          {store.description}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-1 ml-2 shrink-0">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2"
                        onClick={() => {
                          setShowStoreManage(false);
                          handleOpenEditStore(store);
                        }}
                      >
                        编辑
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2 text-destructive hover:text-destructive"
                        onClick={() => storeDeleteMutation.mutate(store.id)}
                        disabled={storeDeleteMutation.isPending}
                      >
                        删除
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStoreManage(false)}>
              关闭
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 记忆实例创建/编辑弹窗 */}
      <Dialog open={showStoreForm} onOpenChange={setShowStoreForm}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingStore ? '编辑记忆库' : '新建记忆库'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {editingStore && (
              <div className="space-y-2">
                <label className="text-sm text-muted-foreground block">唯一 ID（不可修改）</label>
                <div className="text-sm font-mono bg-muted p-2 rounded">{editingStore.id}</div>
              </div>
            )}
            <div className="space-y-2">
              <label className="text-sm font-medium block">名称 *</label>
              <Input
                placeholder="输入记忆库名称"
                value={storeFormData.name}
                onChange={(e) => setStoreFormData((prev) => ({ ...prev, name: e.target.value }))}
                maxLength={200}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium block">描述</label>
              <Textarea
                placeholder="可选，简单描述该记忆库的用途"
                value={storeFormData.description}
                onChange={(e) =>
                  setStoreFormData((prev) => ({ ...prev, description: e.target.value }))
                }
                rows={3}
                maxLength={500}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStoreForm(false)}>
              取消
            </Button>
            <Button
              onClick={() => storeSubmitMutation.mutate()}
              disabled={!storeFormData.name.trim() || storeSubmitMutation.isPending}
            >
              {storeSubmitMutation.isPending && <LoaderIcon className="h-4 w-4 mr-1" />}
              {editingStore ? '保存' : '创建'}
            </Button>
          </DialogFooter>
          {storeSubmitMutation.isError && (
            <p className="text-sm text-destructive">
              {storeSubmitMutation.error instanceof Error
                ? storeSubmitMutation.error.message
                : '保存失败'}
            </p>
          )}
        </DialogContent>
      </Dialog>

      {/* 创建记忆弹窗 */}
      <AlertDialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <AlertDialogContent className="max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle>新增记忆</AlertDialogTitle>
            <AlertDialogDescription>
              向记忆库{' '}
              <span
                className="font-mono inline-block max-w-full truncate align-bottom"
                title={activeStore?.name || activeStoreId || ''}
              >
                {activeStore?.name || activeStoreId}
              </span>{' '}
              添加一条新的记忆
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-4 my-4">
            <div className="space-y-1.5">
              <label className="text-sm font-medium block">记忆内容 *</label>
              <Textarea
                value={formMemory}
                onChange={(e) => setFormMemory(e.target.value)}
                placeholder="请输入记忆文本"
                rows={6}
              />
            </div>
            <div className="flex items-center gap-3">
              <label className="text-sm">infer</label>
              <input
                type="checkbox"
                checked={formInfer}
                onChange={(e) => setFormInfer(e.target.checked)}
                className="h-4 w-4 cursor-pointer"
              />
              <span className="text-xs text-muted-foreground">
                开启后内容会发送给 LLM 提取生成记忆
              </span>
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>取消</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => createMutation.mutate()}
              disabled={!formMemory.trim() || createMutation.isPending}
            >
              {createMutation.isPending && <LoaderIcon className="h-4 w-4 mr-1" />}
              创建
            </AlertDialogAction>
          </AlertDialogFooter>
          {createMutation.isError && (
            <p className="text-sm text-destructive mt-2">
              {createMutation.error instanceof Error ? createMutation.error.message : '创建失败'}
            </p>
          )}
        </AlertDialogContent>
      </AlertDialog>

      {/* 编辑记忆弹窗 */}
      <AlertDialog open={!!editingItem} onOpenChange={(open) => !open && setEditingItem(null)}>
        <AlertDialogContent className="max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle>编辑记忆</AlertDialogTitle>
            <AlertDialogDescription>
              ID: <span className="font-mono text-xs">{editingItem?.id}</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="my-4 space-y-4">
            <div className="space-y-1.5">
              <label className="text-sm font-medium block">记忆内容</label>
              <Textarea
                value={formMemory}
                onChange={(e) => setFormMemory(e.target.value)}
                placeholder="请输入记忆文本"
                rows={6}
              />
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>取消</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => updateMutation.mutate()}
              disabled={!formMemory.trim() || updateMutation.isPending}
            >
              {updateMutation.isPending && <LoaderIcon className="h-4 w-4 mr-1" />}
              更新
            </AlertDialogAction>
          </AlertDialogFooter>
          {updateMutation.isError && (
            <p className="text-sm text-destructive mt-2">
              {updateMutation.error instanceof Error ? updateMutation.error.message : '更新失败'}
            </p>
          )}
        </AlertDialogContent>
      </AlertDialog>

      {/* 删除确认弹窗 */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>确认删除</AlertDialogTitle>
            <AlertDialogDescription>确定要删除这条记忆吗？此操作不可撤销。</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>取消</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteMutation.mutate()}
              disabled={deleteMutation.isPending}
              className="bg-destructive hover:bg-destructive/90"
            >
              {deleteMutation.isPending && <LoaderIcon className="h-4 w-4 mr-1" />}
              删除
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* 详情弹窗 */}
      <AlertDialog open={!!detailItem} onOpenChange={(open) => !open && setDetailItem(null)}>
        <AlertDialogContent className="max-w-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>记忆详情</AlertDialogTitle>
          </AlertDialogHeader>
          {detailItem && (
            <div className="space-y-4 my-4">
              <div>
                <label className="text-sm text-muted-foreground block">ID</label>
                <div className="mt-1 text-sm font-mono">{detailItem.id}</div>
              </div>
              <div>
                <label className="text-sm text-muted-foreground block">内容</label>
                <div className="mt-1 p-3 bg-muted rounded-md text-sm whitespace-pre-wrap">
                  {detailItem.memory}
                </div>
              </div>
              <div>
                <label className="text-sm text-muted-foreground block">分数</label>
                <div className="mt-1 text-sm">
                  {typeof detailItem.score === 'number' ? detailItem.score.toFixed(3) : '-'}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-muted-foreground block">创建时间</label>
                  <div className="mt-1 text-sm">{formatDate(detailItem.created_at)}</div>
                </div>
                <div>
                  <label className="text-sm text-muted-foreground block">更新时间</label>
                  <div className="mt-1 text-sm">{formatDate(detailItem.updated_at)}</div>
                </div>
              </div>
              {detailItem.metadata && Object.keys(detailItem.metadata).length > 0 && (
                <div>
                  <label className="text-sm text-muted-foreground block">元数据</label>
                  <pre className="mt-1 p-3 bg-muted rounded-md text-xs overflow-auto max-h-60">
                    {JSON.stringify(detailItem.metadata, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel>关闭</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
